import {
  Box3,
  Camera,
  Frustum,
  Matrix4,
  Object3D,
  OrthographicCamera,
  PerspectiveCamera,
  Vector3,
} from 'three';

import { AssetManager } from '../asset-manager';
import cameraManager from '../camera-manager';
import { CoordinateManager } from '../coordinate-manager';
import { Cluster } from '../elements/cluster';
import { Design } from '../elements/design';
import { Dxf } from '../elements/dxf';
import { ElementObj } from '../elements/element-obj';
import { Glb } from '../elements/glb';
import { Poi } from '../elements/poi/poi';
import { TerrainModel } from '../elements/terrain-model';
import { VolumeHandler } from '../elements/volume/volume-handler';
import { ProjectGeodataManager } from '../project-geodata-manager';
import { SkyviewScene } from '../scenes/skyview-scene';
import { SkyView } from '../skyview';
import { getReportFrameBounds, ReportFrameState } from '../tools/utils/report-frame';
import { ViewSettings } from '../view-settings';
import { collectTimelineLayers } from './collect-timeline-layers';
import { collectVectorLayers } from './collect-vector-layers';
import { collectVolumeLayer } from './collect-volume-layers';
import { getReportLighting } from './lighting';
import {
  AssetLayer,
  OmittedLayer,
  PaintEntry,
  ReportLayer,
  ReportLighting,
  VectorLayerContent,
} from './manifest';
import { getOmittedLayers, getOmittedModels } from './omitted-layers';
import { getPaintOrder, paintOrderRenderer } from './paint-order';

/** What the report shows: the frame from above, or what the viewer's camera sees in perspective. */
export type ReportRegion =
  | { mode: 'plan'; frame: ReportFrameState }
  | { mode: 'camera'; camera: PerspectiveCamera };

export type ReportLayerOptions = {
  region: ReportRegion;
  includeOrthophoto: boolean;
  blackLinesAndText: boolean;
  /** The file versions of legacy drawings that couldn't be converted to glb, see prepareDrawings. */
  unconvertedVersionIds?: ReadonlySet<string>;
  /** The paper resolution of rasters, which also sizes texts sized on screen. */
  dpi: number;
};

/** A drawing shown from its legacy DXF, whose version needs a glb for the report. */
export type LegacyDrawing = { assetId: string; fileVersionId: string; name: string };

export type ReportLayers = {
  layers: ReportLayer[];
  paintOrder: PaintEntry[];
  omitted: OmittedLayer[];
  lighting: ReportLighting;
  /** The content of the vectors layers' files, by their path in the zip. */
  vectorFiles: Record<string, VectorLayerContent>;
};

type DrawnAsset = Glb | Dxf | Design;

const ORTHOPHOTO_LAYER_ID = 'ortho';

/** Whether the asset is shown in the viewer, with its data loaded. */
const isShown = (asset: ElementObj) =>
  asset.visible && asset.getStateVisibility() && asset.loadingStatus === 'loaded';

const isDrawnAsset = (asset: ElementObj): asset is DrawnAsset =>
  asset instanceof Glb || asset instanceof Dxf || asset instanceof Design;

/** The region in local coordinates. */
type View = {
  intersectsBox: (box: Box3) => boolean;
  containsPoint: (point: Vector3) => boolean;
  /** The camera three.js sorts what is in view with, as it does in the viewer. */
  sortCamera: () => Camera;
  perspective: boolean;
};

function toView(region: ReportRegion, origo: Vector3): View {
  if (region.mode === 'camera') {
    const { camera } = region;
    camera.updateMatrixWorld();
    const frustum = new Frustum().setFromProjectionMatrix(
      new Matrix4().multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse),
    );
    return {
      intersectsBox: (box) => frustum.intersectsBox(box),
      containsPoint: (point) => frustum.containsPoint(point),
      sortCamera: () => camera,
      perspective: true,
    };
  }

  // The north-up box around the frame, at any height
  const bounds = getReportFrameBounds(region.frame);
  const frameBox = new Box3(
    new Vector3(bounds.min.x - origo.x, bounds.min.y - origo.y, -Infinity),
    new Vector3(bounds.max.x - origo.x, bounds.max.y - origo.y, Infinity),
  );
  return {
    intersectsBox: (box) => box.intersectsBox(frameBox),
    containsPoint: (point) =>
      point.x >= frameBox.min.x &&
      point.x <= frameBox.max.x &&
      point.y >= frameBox.min.y &&
      point.y <= frameBox.max.y,
    sortCamera: () => createExportCamera(frameBox),
    perspective: false,
  };
}

const getLocalView = (region: ReportRegion) =>
  toView(region, CoordinateManager.getInstance().getProjectionOrigo() ?? new Vector3());

/** The assets that are shown and drawn in the view. */
function getAssetsInView(view: View): ElementObj[] {
  return AssetManager.instance
    .getAssets(true)
    .filter(
      (asset) =>
        isShown(asset) &&
        !(asset instanceof Poi || asset instanceof Cluster) &&
        view.intersectsBox(new Box3().setFromObject(asset)),
    );
}

/** The assets in the view the backend draws from their glb. */
const getDrawnAssetsInView = (view: View) =>
  getAssetsInView(view).filter(
    (asset): asset is DrawnAsset => isDrawnAsset(asset) && !!asset.assetId,
  );

/** The POIs that are shown with their label, and the clusters of the others. */
const getShownPois = () =>
  AssetManager.instance
    .getAssets(true)
    .filter(
      (asset): asset is Poi | Cluster =>
        (asset instanceof Poi && asset.visible && asset.getStateVisibility() && asset.hasLabel()) ||
        (asset instanceof Cluster && asset.visible),
    );

/**
 * Gets what is shown in the region but can't be in the report, see getOmittedLayers.
 */
export function getOmittedLayersInView(skyview: SkyView, region: ReportRegion) {
  return getOmittedLayers(skyview, getAssetsInView(getLocalView(region)));
}

/**
 * Gets the drawings in the region that are shown from their legacy DXF. Their versions are
 * converted to glb before the report, as the backend draws them from it.
 */
export function getLegacyDrawingsInView(region: ReportRegion): LegacyDrawing[] {
  return getDrawnAssetsInView(getLocalView(region)).flatMap((asset) =>
    asset instanceof Dxf && asset.assetId && asset.selectedVersion
      ? [{ assetId: asset.assetId, fileVersionId: asset.selectedVersion.id, name: asset.name }]
      : [],
  );
}

/** Whether the orthophoto is shown: from above, or in the SkyView scene's perspective. */
export function isOrthophotoInView(skyview: SkyView, perspective: boolean) {
  const scene = skyview.getActiveScene();
  return perspective
    ? scene instanceof SkyviewScene && scene.isOrthophotoVisible()
    : skyview.isOrthophotoInOrthoViewActive();
}

/**
 * Describes the orthophoto, the timeline's tiled model and point cloud, and the assets, terrain
 * models and volumes in the region for the backend to draw, by their ids, and the order and lights
 * to draw them with.
 */
export function collectReportLayers(skyview: SkyView, options: ReportLayerOptions): ReportLayers {
  const origo = CoordinateManager.getInstance().getProjectionOrigo() ?? new Vector3();
  const localToWorld = new Matrix4().makeTranslation(origo);

  const view = toView(options.region, origo);

  const layers: ReportLayer[] = [];
  const omitted: OmittedLayer[] = [];
  const paintOrder: PaintEntry[] = [];

  const container = ProjectGeodataManager.instance().getActiveTimelineContainer();
  const orthotileId =
    ProjectGeodataManager.instance().getActivOrthotileGeodata()?.model?.id ??
    container?.orthotile ??
    undefined;
  if (options.includeOrthophoto && isOrthophotoInView(skyview, view.perspective) && orthotileId) {
    layers.push({
      id: ORTHOPHOTO_LAYER_ID,
      name: container?.displayDate,
      kind: 'orthophoto',
      orthotileId,
      opacity: ViewSettings.instance.orthophoto.opacity,
      ...(view.perspective ? { altitude: ViewSettings.instance.orthophoto.altitude } : {}),
    });
    // The orthophoto is drawn under the assets, see renderOrders.
    paintOrder.push({ layer: ORTHOPHOTO_LAYER_ID });
  }

  // The tiled model and point cloud are the ground the assets are on.
  const timeline = collectTimelineLayers(skyview);
  layers.push(...timeline.layers);
  paintOrder.push(...timeline.layers.map((layer) => ({ layer: layer.id })));
  omitted.push(...timeline.omitted);

  const layerIds = new Map<Object3D, string>();
  const assetsInView = getAssetsInView(view);
  for (const asset of assetsInView.filter((x): x is DrawnAsset => isDrawnAsset(x) && !!x.assetId)) {
    const versionId = asset instanceof Design ? undefined : asset.selectedVersion?.id;
    if (asset instanceof Dxf && versionId && options.unconvertedVersionIds?.has(versionId)) {
      omitted.push({
        kind: 'drawing',
        name: asset.name,
        assetId: asset.assetId,
        fileVersionId: versionId,
      });
      continue;
    }

    // Undefined for DAE files, which are listed as omitted.
    const sceneToLocal = asset.getSceneToLocalMatrix();
    if (!sceneToLocal) {
      continue;
    }

    const layer: AssetLayer = {
      id: `asset-${layerIds.size + 1}`,
      name: asset.name || undefined,
      kind: 'asset',
      assetId: asset.assetId!,
      fileVersionId: versionId,
      matrix: localToWorld.clone().multiply(sceneToLocal).toArray(),
      opacity: asset.opacity,
      // A legacy drawing has no glb loaded, so the backend leaves out the layers off in the file.
      ...(asset instanceof Dxf
        ? {}
        : { hiddenNodes: asset instanceof Glb ? asset.getHiddenNodes() : [] }),
      colorOverride: options.blackLinesAndText ? '#000000' : null,
    };
    layers.push(layer);
    layerIds.set(asset, layer.id);
  }

  // Terrain models and volumes are drawn as rasters, painted whole.
  const wholeLayers = new Set<string>();
  assetsInView.forEach((asset, index) => {
    const sceneToLocal = asset instanceof TerrainModel ? asset.getSceneToLocalMatrix() : undefined;
    if (asset instanceof TerrainModel && sceneToLocal) {
      const id = `terrain-${index + 1}`;
      layers.push({
        id,
        name: asset.name || undefined,
        kind: 'terrainModel',
        geodataId: asset.geoDataId,
        matrix: localToWorld.clone().multiply(sceneToLocal).toArray(),
        opacity: asset.opacity,
      });
      layerIds.set(asset, id);
      wholeLayers.add(id);
    }

    const volume = collectVolumeLayer(asset, `volume-${index + 1}`, origo);
    const volumeModel = (asset as Partial<{ volumeHandler: VolumeHandler }>).volumeHandler
      ?.volumeModel;
    if (volume && volumeModel) {
      layers.push(volume);
      layerIds.set(volumeModel, volume.id);
      wholeLayers.add(volume.id);
    }
  });

  const vectors = collectVectorLayers(assetsInView, getShownPois(), {
    isInView: view.containsPoint,
    perspective: view.perspective,
    origo,
    dpi: options.dpi,
    blackLinesAndText: options.blackLinesAndText,
    renderer: skyview.renderer,
    camera: cameraManager().getActiveCamera()?.getTHREECamera(),
  });
  layers.push(...vectors.layers);
  omitted.push(...getOmittedModels(assetsInView));
  vectors.layerIds.forEach((id, object) => layerIds.set(object, id));

  // Line and area assets and vectors are painted whole, without parts.
  vectors.layers.forEach((layer) => wholeLayers.add(layer.id));
  if (skyview.renderer && layerIds.size > 0) {
    const lists = paintOrderRenderer.readRenderLists(
      skyview.renderer,
      AssetManager.instance,
      view.sortCamera(),
    );
    paintOrder.push(
      ...getPaintOrder(lists, (object) => findLayerId(object, layerIds), wholeLayers),
    );
  }

  // Vectors three.js doesn't draw, or culled, go on top, as the POIs and labels are in the viewer.
  const painted = new Set(paintOrder.map((entry) => entry.layer));
  for (const layer of vectors.layers) {
    if (
      layer.kind === 'vectors' &&
      !painted.has(layer.id) &&
      !vectors.paintedLast.includes(layer.id)
    ) {
      paintOrder.push({ layer: layer.id });
    }
  }
  paintOrder.push(...vectors.paintedLast.map((layer) => ({ layer })));

  return {
    layers,
    paintOrder,
    lighting: getReportLighting(),
    omitted,
    vectorFiles: vectors.files,
  };
}

/** The layer of the asset the object is part of. */
function findLayerId(object: Object3D, layerIds: Map<Object3D, string>) {
  for (let node: Object3D | null = object; node; node = node.parent) {
    const id = layerIds.get(node);
    if (id !== undefined) {
      return id;
    }
  }

  return undefined;
}

/**
 * A top-down camera over the frame, at the height and with the depth range of the viewer's camera,
 * so three.js sorts what's in the frame as it does in the viewer.
 */
function createExportCamera(frameBox: Box3) {
  const viewer = cameraManager().getActiveCamera()?.getTHREECamera();
  const near = viewer instanceof OrthographicCamera ? viewer.near : 0.1;
  const far = viewer instanceof OrthographicCamera ? viewer.far : 100000;

  const halfWidth = (frameBox.max.x - frameBox.min.x) / 2;
  const halfHeight = (frameBox.max.y - frameBox.min.y) / 2;
  const camera = new OrthographicCamera(-halfWidth, halfWidth, halfHeight, -halfHeight, near, far);
  camera.position.set(
    (frameBox.min.x + frameBox.max.x) / 2,
    (frameBox.min.y + frameBox.max.y) / 2,
    viewer?.position.z ?? 1000,
  );
  camera.updateMatrixWorld(true);

  return camera;
}
